import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HelloSign from 'hellosign-embedded';
import PropTypes from 'prop-types';
import { AppModal } from '../AppModal';
import { previewSignIcf, getSignIcfPiUrl, completeSignIcf } from '../../services/lighthouse.service';

/**
 * Sign ICF modal — matches onyxbio/lighthouse mock-image-modal chrome:
 * X close, Official name / Email meta grid, Dropbox Sign document surface in-body.
 *
 * The embed host must stay mounted and sized before HelloSign.open() — the SDK
 * injects an iframe and immediately postMessages it; a display:none / zero-height
 * host yields contentWindow === null.
 */
export function SignIcfReviewModal({ open, onClose, token, userId, onSigned }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [embedReady, setEmbedReady] = useState(false);
  const clientRef = useRef(null);
  const containerRef = useRef(null);
  const completedRef = useRef(false);

  const closeClient = () => {
    if (!clientRef.current) return;
    try {
      clientRef.current.close();
    } catch {
      // already closed
    }
    clientRef.current = null;
  };

  useEffect(() => {
    if (!open) {
      completedRef.current = false;
      setPreview(null);
      setError(null);
      setEmbedReady(false);
      closeClient();
    }
  }, [open]);

  useEffect(() => {
    if (!open || !userId || !token) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setEmbedReady(false);

    (async () => {
      const { data, error: err } = await previewSignIcf(token, userId);
      if (cancelled) return;
      if (err) {
        setLoading(false);
        setError(typeof err === 'string' ? err : err?.message || 'Failed to load ICF');
        return;
      }
      setPreview(data);
      setLoading(false);

      if (!data?.eligible || !data?.document_id) {
        return;
      }

      // Show a sized host, then wait for paint before HelloSign.open().
      setEmbedReady(true);
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      if (cancelled || !containerRef.current) return;

      const { data: signData, error: signErr } = await getSignIcfPiUrl(
        token,
        userId,
        data.document_id
      );
      if (cancelled) return;
      if (signErr || !signData?.sign_url) {
        setEmbedReady(false);
        setError(signErr?.message || signData?.error || 'Failed to open signing URL');
        return;
      }

      closeClient();
      const clientId = signData.client_id || process.env.REACT_APP_DROPBOX_SIGN_CLIENT_ID;
      const client = new HelloSign({ clientId });
      clientRef.current = client;

      client.on('sign', async () => {
        completedRef.current = true;
        try {
          await completeSignIcf(token, userId);
          if (onSigned) onSigned();
        } catch (e) {
          setError(e?.message || 'Failed to finalize countersign');
        } finally {
          onClose();
        }
      });

      client.on('close', () => {
        clientRef.current = null;
      });

      client.on('error', (hsErr) => {
        console.error('HelloSign error', hsErr);
        if (cancelled || completedRef.current) return;
        setError('Signing was interrupted. Close and try again.');
      });

      try {
        client.open(signData.sign_url, {
          clientId,
          skipDomainVerification: true,
          container: containerRef.current,
        });
      } catch (e) {
        if (!cancelled) {
          setEmbedReady(false);
          setError(e?.message || 'Failed to open Dropbox Sign');
        }
      }
    })();

    return () => {
      cancelled = true;
      closeClient();
    };
  }, [open, userId, token, onClose, onSigned]);

  const officialName = preview?.name || '—';
  const dob = preview?.dob || '—';
  const email = preview?.email || '—';
  // Keep the host sized for the whole session — toggling display:none after
  // HelloSign.open() tears down contentWindow and triggers postMessage errors.
  const showEmbed = embedReady;

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title={null}
      showCloseButton={false}
      actions={null}
      dividers={false}
      maxWidth="lg"
      className="onyx-document-modal"
      aria-label="Sign ICF"
    >
      <div className="onyx-sign-icf" data-testid="onyx-sign-icf">
        <IconButton
          aria-label="Close"
          onClick={onClose}
          className="onyx-sign-icf__close"
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <div className="onyx-sign-icf__header">
          <span className="onyx-sign-icf__meta-label">Official name</span>
          <span className="onyx-sign-icf__meta-value">{officialName}</span>
          <span className="onyx-sign-icf__meta-label">DOB</span>
          <span className="onyx-sign-icf__meta-value">{dob}</span>
          <span className="onyx-sign-icf__meta-label">Email</span>
          <span className="onyx-sign-icf__meta-value">{email}</span>
        </div>

        <div className="onyx-sign-icf__document">
          {loading ? (
            <div className="onyx-sign-icf__status">
              <CircularProgress size={28} />
              <Typography variant="uiBody">Loading ICF…</Typography>
            </div>
          ) : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
          {!loading && preview && !preview.eligible ? (
            <Alert severity="info">No PI-signable consent for this member.</Alert>
          ) : null}
          <div
            ref={containerRef}
            className={`onyx-sign-icf__embed${showEmbed ? ' onyx-sign-icf__embed--active' : ''}`}
            data-testid="onyx-sign-icf-embed"
            aria-hidden={!showEmbed}
          />
        </div>
      </div>
    </AppModal>
  );
}

SignIcfReviewModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  token: PropTypes.string,
  userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onSigned: PropTypes.func,
};
